import { useEffect, useState } from "react";
import { useGlobalViewMode } from "../components/ViewModeToggle";

import { PageHeader } from "../components/GradientCard";import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo } from "../lib/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Eye, CalendarClock, MessageSquare, Send, Copy, ExternalLink, AlertTriangle, CheckCircle2, XCircle, ArrowRight, PhoneCall, FileText, Trash2, ClipboardList, UsersRound, Pencil, Upload, Paperclip
} from "lucide-react";

const ROUNDS = ["HR Round", "Technical Round", "Managerial Round", "Final Round", "Physical Interview"];
const RESULTS = ["Selected", "Selected for Next Round", "On Hold", "Rejected"];
const RATINGS = ["Excellent", "Good", "Average", "Poor"];
const RECOMMENDATIONS = ["Strongly Recommend", "Recommend", "Hold", "Not Recommend"];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

export default function Interview({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  // Resolve effective access level on the Interview module:
  //   master_admin / hr_admin → always "write"
  //   else → user.module_access_levels.hr_interview (write | edit | view)
  const accessLevel = (() => {
    if (user?.role === "master_admin") return "write";
    if (moduleCanDo(user, "hr", "delete")) return "write";
    if (moduleCanDo(user, "hr", "edit"))   return "edit";
    return (user?.module_access_levels || {})["hr_interview"] || "view";
  })();
  const canEdit = accessLevel === "edit" || accessLevel === "write";
  const canDelete = accessLevel === "write";
  // Legacy alias still used in child components (Schedule Round / Move-to-onboarding etc.)
  // — keeps "Write" parity with master admin, "Edit" can also schedule rounds & record feedback,
  // pure "View" cannot.
  const isAdmin = canEdit;
  const [searchParams, setSearchParams] = useSearchParams();
  const [interviewees, setInterviewees] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingDeleteIE, setPendingDeleteIE] = useState(null);

  const load = async () => {
    // Each endpoint is fetched independently so a 403 on one (e.g. /employees
    // which is Master-Admin only and only needed for the Schedule-Round
    // interviewer dropdown) does NOT prevent View/Edit users from seeing the
    // candidate list + interviews + lead sources.
    const safe = async (p, setter) => {
      try {
        const { data } = await p;
        setter(data || []);
      } catch (e) {
        // 403 on /employees for non-admins is expected — swallow silently.
        // Surface only unexpected failures.
        if (e?.response?.status && e.response.status !== 403) {
          toast.error(formatError(e));
        }
      }
    };
    await Promise.all([
      safe(api.get("/interviewees"), setInterviewees),
      safe(api.get("/interviews"), setInterviews),
      safe(api.get("/lead-sources"), setLeadSources),
      isAdmin ? safe(api.get("/employees"), setEmployees) : Promise.resolve(),
    ]);
  };
  useEffect(() => { load(); }, []);

  // Auto-open the interviewee detail dialog when ?open=<id> is in the URL
  // (used by the Dashboard upcoming-interviews widget).
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || viewing || !interviewees.length) return;
    const ie = interviewees.find((x) => x.id === openId);
    if (ie) {
      setViewing(ie);
      // Clear the query param so back/forward navigation doesn't keep re-opening
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    } else {
      // Fall back to a direct fetch in case the id isn't in the current list view
      api.get(`/interviewees/${openId}`).then(({ data }) => setViewing(data)).catch(() => {});
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [interviewees, searchParams, viewing, setSearchParams]);

  const interviewsForIE = (id) => interviews.filter((i) => i.interviewee_id === id);

  return (
    <div className="space-y-6" data-testid="interview-page">
      {!embedded && (
        <PageHeader
          moduleKey="hr"
          tone="violet"
          icon={UsersRound}
          eyebrow="HR Module"
          title="Interview"
          subtitle="Capture candidates, schedule interview rounds, record feedback, and decide next step."
          actions={
            <Button onClick={() => setShowAdd(true)} data-testid="add-interviewee-btn">
              <Plus size={14} className="mr-1.5" /> Add Interviewee
            </Button>
          }
        />
      )}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={() => setShowAdd(true)} data-testid="add-interviewee-btn">
            <Plus size={14} className="mr-1.5" /> Add Interviewee
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="pk-card overflow-hidden">
        {viewMode === "card" ? (
          interviewees.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No interviewees yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="ie-cards">
              {interviewees.map((ie) => (
                <div key={ie.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`ie-card-${ie.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 truncate">{ie.name}</div>
                      <div className="text-[10.5px] mono text-slate-500 truncate">{ie.personal_email} · {ie.mobile_number}</div>
                    </div>
                    <StatusBadge status={ie.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Position</div><div className="text-slate-800 truncate">{ie.position || "—"}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Source</div><div className="text-slate-700 truncate">{ie.source_of_lead || "—"}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Current Round</div><div className="text-slate-800 truncate">{ie.current_round || "—"}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Created By</div><div className="text-slate-700 truncate">{ie.created_by_name}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                    <Button size="sm" variant="outline" onClick={() => setViewing(ie)} className="h-7 text-[10.5px]" data-testid={`ie-view-${ie.id}`}>
                      <Eye size={11} className="mr-1" /> Open
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => setEditing(ie)} className="h-7 text-[10.5px]" data-testid={`ie-edit-${ie.id}`}>
                        <Pencil size={11} className="mr-1" /> Edit
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingDeleteIE(ie)}
                        className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid={`ie-delete-${ie.id}`}
                      >
                        <Trash2 size={11} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Candidate</th>
                <th className="px-4 py-3 font-semibold">Position</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Current Round</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Created By</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {interviewees.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No interviewees yet.</td></tr>
              )}
              {interviewees.map((ie) => (
                <tr key={ie.id} className="border-b border-slate-100" data-testid={`ie-row-${ie.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{ie.name}</div>
                    <div className="text-[10.5px] text-slate-500 mono">{ie.personal_email} · {ie.mobile_number}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{ie.position || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-[12px]">{ie.source_of_lead || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{ie.current_round || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={ie.status} /></td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">{ie.created_by_name}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setViewing(ie)} className="h-8 text-[11px]" data-testid={`ie-view-${ie.id}`}>
                        <Eye size={12} className="mr-1" /> Open
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => setEditing(ie)} className="h-8 text-[11px]" data-testid={`ie-edit-${ie.id}`}>
                          <Pencil size={12} className="mr-1" /> Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPendingDeleteIE(ie)}
                          className="h-8 text-[11px] text-rose-600"
                          data-testid={`ie-delete-${ie.id}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <AddInterviewee
        open={showAdd}
        onClose={() => setShowAdd(false)}
        leadSources={leadSources}
        onReloadLeadSources={async () => {
          const { data } = await api.get("/lead-sources");
          setLeadSources(data || []);
        }}
        onSaved={() => { load(); setShowAdd(false); }}
      />

      {viewing && (
        <InterviewDetail
          viewing={viewing}
          onClose={() => setViewing(null)}
          interviewsForIE={interviewsForIE(viewing.id)}
          employees={employees}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          onReload={async () => {
            await load();
            const { data } = await api.get(`/interviewees/${viewing.id}`);
            setViewing(data);
          }}
        />
      )}
      {editing && (
        <EditInterviewee
          interviewee={editing}
          leadSources={leadSources}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); setEditing(null); }}
        />
      )}
      <DeleteWithReasonDialog
        open={!!pendingDeleteIE}
        onClose={() => setPendingDeleteIE(null)}
        title={pendingDeleteIE ? `Delete candidate "${pendingDeleteIE.name}"?` : "Delete candidate?"}
        description="All interview rounds & onboarding records for this candidate will be removed. The record moves to Admin Hub → Recycle Bin; Master Admin can restore it later."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/interviewees/${pendingDeleteIE.id}`, { data: { reason } });
            toast.success("Candidate moved to Recycle Bin");
            load();
          } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); throw err; }
        }}
        testid="ie-delete"
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: ["bg-slate-100 text-slate-700", "New"],
    in_interview: ["bg-violet-100 text-violet-800", "In Interview"],
    feedback_recorded: ["bg-sky-100 text-sky-800", "Feedback Recorded"],
    in_onboarding: ["bg-emerald-100 text-emerald-800", "In Onboarding"],
    onboarded: ["bg-green-200 text-green-900", "Onboarded"],
    no_show: ["bg-rose-100 text-rose-800", "No Show"],
    out_of_budget: ["bg-rose-200 text-rose-900", "Out of Budget"],
  };
  const [cls, lbl] = map[status] || ["bg-slate-100 text-slate-700", status];
  return <Badge className={cls}>{lbl}</Badge>;
}

// =============== Add Interviewee dialog ===============
function AddInterviewee({ open, onClose, leadSources, onReloadLeadSources, onSaved }) {
  const [form, setForm] = useState({
    name: "", personal_email: "", mobile_number: "",
    position: "", resume_link: "", source_of_lead: "",
  });
  const [newSource, setNewSource] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", personal_email: "", mobile_number: "", position: "", resume_link: "", source_of_lead: "" });
  }, [open]);

  const addSource = async () => {
    const name = newSource.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/lead-sources", { name });
      toast.success("Source added");
      await onReloadLeadSources();
      setForm((f) => ({ ...f, source_of_lead: data.name }));
      setNewSource("");
    } catch (e) { toast.error(formatError(e)); }
  };

  const submit = async () => {
    for (const f of ["name", "personal_email", "mobile_number"]) {
      if (!form[f].trim()) { toast.error(`${f.replace("_", " ")} is required`); return; }
    }
    setBusy(true);
    try {
      await api.post("/interviewees", form);
      toast.success("Interviewee added");
      onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="add-ie-dialog">
        <DialogHeader><DialogTitle className="heading">Add Interviewee</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <Fld label="Name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ie-name" autoFocus />
          </Fld>
          <Fld label="Position">
            <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g. Senior Developer" data-testid="ie-position" />
          </Fld>
          <Fld label="Personal Email *">
            <Input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} data-testid="ie-email" />
          </Fld>
          <Fld label="Mobile Number *">
            <Input value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} placeholder="+91…" data-testid="ie-mobile" />
          </Fld>
          <Fld label="Resume Link">
            <Input value={form.resume_link} onChange={(e) => setForm({ ...form, resume_link: e.target.value })} placeholder="https://…" data-testid="ie-resume" />
          </Fld>
          <Fld label="Source of Lead">
            <Select value={form.source_of_lead} onValueChange={(v) => setForm({ ...form, source_of_lead: v })}>
              <SelectTrigger data-testid="ie-source"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {leadSources.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5 mt-1.5">
              <Input value={newSource} onChange={(e) => setNewSource(e.target.value)} placeholder="+ Add new source…" className="h-8 text-[11px]" data-testid="ie-new-source" />
              <Button size="sm" variant="outline" onClick={addSource} className="h-8 text-[11px]" data-testid="ie-add-source-btn">Add</Button>
            </div>
          </Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)]" data-testid="ie-submit">
            {busy ? "Saving…" : "Add Interviewee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============== Interview Detail dialog ===============
export function InterviewDetail({ viewing, onClose, interviewsForIE, employees, isAdmin, currentUserId, onReload }) {
  const [tab, setTab] = useState("details");
  const [showSchedule, setShowSchedule] = useState(false);
  const [feedbackXid, setFeedbackXid] = useState(null);
  const [viewFeedbackXid, setViewFeedbackXid] = useState(null);
  const [showMove, setShowMove] = useState(false);
  const [viewSamplesXid, setViewSamplesXid] = useState(null);
  const [remarkText, setRemarkText] = useState("");

  const addRemark = async () => {
    if (!remarkText.trim()) return;
    try {
      await api.post(`/interviewees/${viewing.id}/remark`, { text: remarkText.trim() });
      toast.success("Remark added");
      setRemarkText("");
      onReload();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Dialog open={!!viewing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ie-detail-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            {viewing.name} <StatusBadge status={viewing.status} />
          </DialogTitle>
          <div className="text-[12px] text-slate-600">
            {viewing.position || "—"} · {viewing.personal_email} · {viewing.mobile_number}
            {viewing.resume_link && (
              <a href={viewing.resume_link} target="_blank" rel="noreferrer" className="ml-2 text-violet-700 hover:underline inline-flex items-center gap-1">
                <ExternalLink size={11} /> Resume
              </a>
            )}
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="details" data-testid="ie-tab-details">Candidate Details</TabsTrigger>
            <TabsTrigger value="rounds" data-testid="ie-tab-rounds">Rounds & Feedback</TabsTrigger>
            <TabsTrigger value="remarks" data-testid="ie-tab-remarks">Remarks ({(viewing.remarks || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="pt-3" data-testid="ie-details-pane">
            <CandidateDetailsView viewing={viewing} />
          </TabsContent>

          <TabsContent value="rounds" className="space-y-3 pt-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Interview Rounds</div>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowSchedule(true)} className="bg-violet-600 hover:bg-violet-700 h-8 text-[11px]" data-testid="ie-schedule-btn">
                  <Plus size={12} className="mr-1" /> Schedule Round
                </Button>
              )}
            </div>

            {interviewsForIE.length === 0 ? (
              <div className="text-slate-400 text-sm py-6 text-center italic">No rounds scheduled. Click "Schedule Round" to start.</div>
            ) : (
              <ul className="space-y-2">
                {interviewsForIE.map((x) => (
                  <li key={x.id} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`round-${x.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-semibold text-slate-800 flex items-center gap-2">
                          {x.round}
                          <Badge className={
                            x.status === "done" ? "bg-emerald-100 text-emerald-800" :
                            x.status === "no_show" ? "bg-rose-100 text-rose-800" :
                            x.status === "cancelled" ? "bg-slate-200 text-slate-700" :
                            "bg-amber-100 text-amber-800"
                          }>{x.status}</Badge>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> {fmtDT(x.scheduled_at)}</span>
                          <span>· Interviewer: <b className="text-slate-700">{x.interviewer_name}</b></span>
                          {x.meeting_url ? (
                            <a href={x.meeting_url} target="_blank" rel="noreferrer" className="text-violet-700 hover:underline inline-flex items-center gap-0.5">
                              <ExternalLink size={11} /> Meeting Link
                            </a>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]">Meeting Link to be sent</Badge>
                          )}
                          {x.meeting_url && !x.meeting_url_sent && (
                            <Badge className="bg-rose-100 text-rose-800 text-[10px]">Send link to candidate</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={() => {
                          const tpl = `Hi ${viewing.name},\nYou have been invited for ${x.round} interview on ${fmtDT(x.scheduled_at)} with ${x.interviewer_name}.\n${x.meeting_url ? `Join here: ${x.meeting_url}` : ""}\n— Panchakosh Ventures HR`;
                          navigator.clipboard.writeText(tpl).then(() => toast.success("Message copied — paste to WhatsApp/Email"));
                        }} className="h-7 text-[10.5px]" data-testid={`round-copy-${x.id}`}>
                          <Copy size={11} className="mr-1" /> Copy invite text
                        </Button>
                        {x.status === "scheduled" && !x.meeting_url_sent && (
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              await api.patch(`/interviews/${x.id}`, { meeting_url_sent: true });
                              toast.success("Marked as sent to candidate");
                              onReload();
                            } catch (e) { toast.error(formatError(e)); }
                          }} className="h-7 text-[10.5px] border-emerald-300 text-emerald-700" data-testid={`round-sent-${x.id}`}>
                            <Send size={11} className="mr-1" /> Sent to Interviewee
                          </Button>
                        )}
                        {x.sample_questions && (
                          <Button size="sm" variant="outline" onClick={() => setViewSamplesXid(x.id)} className="h-7 text-[10.5px]" data-testid={`round-view-samples-${x.id}`}>
                            <FileText size={11} className="mr-1" /> View Sample Questions
                          </Button>
                        )}
                        {x.status === "scheduled" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setFeedbackXid(x.id)} className="h-7 text-[10.5px] border-emerald-300 text-emerald-700" data-testid={`round-done-${x.id}`}>
                              <CheckCircle2 size={11} className="mr-1" /> Mark Done & Feedback
                            </Button>
                            <Button size="sm" variant="outline" onClick={async () => {
                              const remark = window.prompt("Selected — add a brief remark (optional):", "");
                              if (remark === null) return;
                              try {
                                await api.post(`/interviews/${x.id}/feedback`, { feedback: { result: "Selected", remark, overall_rating: 0 } });
                                toast.success("Marked Selected");
                                onReload();
                              } catch (e) { toast.error(formatError(e)); }
                            }} className="h-7 text-[10.5px] border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" data-testid={`round-selected-${x.id}`}>
                              <CheckCircle2 size={11} className="mr-1" /> Selected w/ Remark
                            </Button>
                            <Button size="sm" variant="outline" onClick={async () => {
                              const remark = window.prompt("Rejected — add the reason (required):", "");
                              if (remark === null) return;
                              if (!remark.trim()) { toast.error("A remark is required when rejecting"); return; }
                              try {
                                await api.post(`/interviews/${x.id}/feedback`, { feedback: { result: "Rejected", remark, overall_rating: 0 } });
                                toast.success("Marked Rejected");
                                onReload();
                              } catch (e) { toast.error(formatError(e)); }
                            }} className="h-7 text-[10.5px] border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100" data-testid={`round-rejected-${x.id}`}>
                              <XCircle size={11} className="mr-1" /> Rejected w/ Remark
                            </Button>
                            <Button size="sm" variant="outline" onClick={async () => {
                              if (!window.confirm("Mark this interview as No Show?")) return;
                              try { await api.post(`/interviews/${x.id}/no-show`); toast.success("Marked no-show"); onReload(); }
                              catch (e) { toast.error(formatError(e)); }
                            }} className="h-7 text-[10.5px] text-rose-700" data-testid={`round-noshow-${x.id}`}>
                              <XCircle size={11} className="mr-1" /> No Show
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={async () => {
                          if (!window.confirm(`Delete this ${x.round}? This cannot be undone.`)) return;
                          try {
                            await api.delete(`/interviews/${x.id}`);
                            toast.success("Round deleted");
                            onReload();
                          } catch (err) { toast.error(formatError(err)); }
                        }} className="h-7 text-[10.5px] text-rose-700 hover:bg-rose-50" data-testid={`round-delete-${x.id}`}>
                          <Trash2 size={11} />
                        </Button>
                      </div>
                    </div>

                    {/* Quick edit row */}
                    {x.status === "scheduled" && (
                      <RoundQuickEdit interview={x} onSaved={onReload} />
                    )}

                    {/* Feedback summary */}
                    {x.status === "done" && x.feedback?.result && (
                      <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[12px] flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <b>Result:</b> {x.feedback.result} · <b>Rating:</b> {x.feedback.overall_rating}/10
                          {x.feedback.recommendation && <> · <b>Rec:</b> {x.feedback.recommendation}</>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewFeedbackXid(x.id)}
                          className="h-7 text-[10.5px] border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          data-testid={`round-view-feedback-${x.id}`}
                        >
                          <ClipboardList size={11} className="mr-1" /> View Feedback
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Move actions for HR/Master */}
            {isAdmin && interviewsForIE.some((x) => x.status === "done") && viewing.status !== "in_onboarding" && viewing.status !== "onboarded" && (
              <div className="border-t border-slate-100 pt-3 mt-3 flex items-center justify-end gap-2">
                <Button size="sm" onClick={() => setShowMove(true)} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="ie-move-onb">
                  <ArrowRight size={12} className="mr-1.5" /> Move to Onboarding
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="remarks" className="space-y-2 pt-3">
            <div className="flex items-center gap-2">
              <Input value={remarkText} onChange={(e) => setRemarkText(e.target.value)} placeholder="Add a remark…" data-testid="ie-remark-input" />
              <Button size="sm" onClick={addRemark} data-testid="ie-remark-add"><MessageSquare size={12} className="mr-1" />Add</Button>
            </div>
            {(viewing.remarks || []).length === 0 ? (
              <div className="text-slate-400 text-sm py-4 text-center italic">No remarks yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {(viewing.remarks || []).slice().reverse().map((r, i) => (
                  <li key={i} className="border border-slate-200 rounded-md p-2.5 bg-white">
                    <div className="text-[11px] text-slate-500">{r.by_name} · {fmtDT(r.at)}</div>
                    <div className="text-[13px] text-slate-800 mt-0.5">{r.text}</div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>

        {showSchedule && (
          <ScheduleRoundDialog
            ieId={viewing.id}
            ieName={viewing.name}
            employees={employees}
            onClose={() => setShowSchedule(false)}
            onSaved={() => { setShowSchedule(false); onReload(); }}
          />
        )}
        {feedbackXid && (
          <FeedbackDialog
            xid={feedbackXid}
            interview={interviewsForIE.find((x) => x.id === feedbackXid)}
            onClose={() => setFeedbackXid(null)}
            onSaved={() => { setFeedbackXid(null); onReload(); }}
          />
        )}
        {viewFeedbackXid && (
          <ViewFeedbackDialog
            interview={interviewsForIE.find((x) => x.id === viewFeedbackXid)}
            onClose={() => setViewFeedbackXid(null)}
          />
        )}
        {viewSamplesXid && (
          <Dialog open={true} onOpenChange={(o) => !o && setViewSamplesXid(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="view-samples-dialog">
              <DialogHeader>
                <DialogTitle className="heading">
                  Sample Questions — {interviewsForIE.find((x) => x.id === viewSamplesXid)?.round}
                </DialogTitle>
              </DialogHeader>
              <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed font-sans bg-slate-50 border border-slate-200 rounded p-4" data-testid="samples-content">
                {interviewsForIE.find((x) => x.id === viewSamplesXid)?.sample_questions || "—"}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(interviewsForIE.find((x) => x.id === viewSamplesXid)?.sample_questions || "")
                    .then(() => toast.success("Copied"));
                }}><Copy size={12} className="mr-1.5" /> Copy</Button>
                <Button onClick={() => setViewSamplesXid(null)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {showMove && (
          <MoveToOnboardingDialog
            ie={viewing}
            onClose={() => setShowMove(false)}
            onSaved={() => { setShowMove(false); onReload(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RoundQuickEdit({ interview, onSaved }) {
  const [meeting, setMeeting] = useState(interview.meeting_url || "");
  const [samples, setSamples] = useState(interview.sample_questions || "");
  const save = async () => {
    try {
      await api.patch(`/interviews/${interview.id}`, {
        meeting_url: meeting,
        sample_questions: samples,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="mt-2 border-t border-slate-100 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11.5px]">
      <div>
        <Label className="text-[10px] uppercase tracking-wider">Meeting URL</Label>
        <Input value={meeting} onChange={(e) => setMeeting(e.target.value)} placeholder="https://meet.google.com/…" className="h-8 text-[11.5px]" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider">Sample Questions (visible to interviewer)</Label>
        <Textarea value={samples} onChange={(e) => setSamples(e.target.value)} placeholder="Q1…&#10;Q2…" rows={2} className="text-[11.5px]" />
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <Button size="sm" onClick={save} className="h-7 text-[10.5px]">Save</Button>
      </div>
    </div>
  );
}

function ScheduleRoundDialog({ ieId, ieName, employees, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "master_admin" || moduleCanDo(user, "hr", "edit");
  const [calledBefore, setCalledBefore] = useState(""); // "" | "yes" | "no"
  const [callStatus, setCallStatus] = useState("");      // "Call Not Answered" | "Not Interested" | "SPAM" | "Schedule the Interview"
  const [callRemark, setCallRemark] = useState("");
  // Date + 12-hour time pieces (combined into scheduled_at on submit)
  const [dateOnly, setDateOnly] = useState("");
  const [hour12, setHour12] = useState("10");  // 1..12
  const [minute, setMinute] = useState("00");  // "00", "05", … "55"
  const [ampm, setAmPm] = useState("AM");
  const [form, setForm] = useState({
    round: "HR Round", interviewer_id: "",
    meeting_url: "", sample_questions: "",
  });

  const scheduledAt = (() => {
    if (!dateOnly) return "";
    let h = parseInt(hour12, 10) || 0;
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    const hh = String(h).padStart(2, "0");
    const mm = (minute || "00").padStart(2, "0");
    return `${dateOnly}T${hh}:${mm}:00`;
  })();

  // Show meeting form only when we have a clear "go" signal:
  //  - Caller said YES they already called, OR
  //  - Caller said NO + recorded the call + status === "Schedule the Interview"
  const canSchedule =
    calledBefore === "yes"
    || (calledBefore === "no" && callStatus === "Schedule the Interview" && callRemark.trim());

  // Helper: save the call attempt as a remark on the interviewee (used when caller says NO + non-Schedule status)
  const recordCallAttempt = async (status, remark) => {
    try {
      await api.post(`/interviewees/${ieId}/remark`, {
        text: `Call attempt before scheduling — Status: ${status}. Note: ${remark || "(no note)"}`,
      });
    } catch (e) { toast.error(formatError(e)); throw e; }
  };

  const submit = async () => {
    // Gate: enforce pre-call workflow
    if (!calledBefore) { toast.error("Please confirm whether you have called the interviewee"); return; }
    if (calledBefore === "no" && !callStatus) { toast.error("Pick the call status after calling the candidate"); return; }
    if (calledBefore === "no" && !callRemark.trim()) { toast.error("Add a remark for the call attempt"); return; }

    // If non-schedule call outcomes — record the remark and exit without creating a round
    if (calledBefore === "no" && callStatus && callStatus !== "Schedule the Interview") {
      try {
        await recordCallAttempt(callStatus, callRemark);
        toast.success(`Recorded: ${callStatus}`);
        onSaved();
      } catch { /* toast already shown */ }
      return;
    }

    // Otherwise validate the schedule
    if (!form.interviewer_id) return toast.error("Pick an interviewer");
    if (!scheduledAt) return toast.error("Pick date & time");
    // Physical Interview rounds skip the online meeting link + sample
    // questions — the interview happens on-site, only date/time/interviewer
    // are required.
    const isPhysical = form.round === "Physical Interview";
    if (!isPhysical && isAdmin && !form.meeting_url.trim()) return toast.error("Meeting link is mandatory for HR / Master Admin");
    if (!isPhysical && !form.sample_questions.trim()) return toast.error("Please upload sample questions for the interviewer");

    try {
      if (calledBefore === "no") {
        await recordCallAttempt("Schedule the Interview", callRemark);
      }
      await api.post("/interviews", {
        ...form,
        scheduled_at: scheduledAt,
        interviewee_id: ieId,
        called_interviewee: true,
        call_remark: callRemark || "",
      });
      toast.success("Round scheduled");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="schedule-round-dialog">
        <DialogHeader><DialogTitle className="heading">Schedule Round — {ieName}</DialogTitle></DialogHeader>

        {/* Step 1 — Did you call? */}
        <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 mb-2">
          <Label className="text-[11.5px] font-bold text-violet-900">
            Did you call the interviewee before scheduling? *
          </Label>
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant={calledBefore === "yes" ? "default" : "outline"}
              className={`h-8 text-[11px] ${calledBefore === "yes" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={() => { setCalledBefore("yes"); setCallStatus(""); setCallRemark(""); }}
              disabled={calledBefore === "no"}
              title={calledBefore === "no" ? "You already indicated you haven't called the candidate yet — please complete the call and pick an outcome below." : ""}
              data-testid="sr-called-yes"
            >Yes — already called</Button>
            <Button
              size="sm"
              variant={calledBefore === "no" ? "default" : "outline"}
              className={`h-8 text-[11px] ${calledBefore === "no" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
              onClick={() => setCalledBefore("no")}
              data-testid="sr-called-no"
            >No — not yet</Button>
          </div>
          {calledBefore === "no" && (
            <div className="text-[10.5px] text-amber-800 mt-1.5 italic">
              You've indicated you haven't called the candidate yet. To preserve audit integrity,
              you can't switch back to "Yes — already called" — please complete the call and pick
              the outcome below.
            </div>
          )}
        </div>

        {/* Step 2 — If No: lock + ask for call outcome + remark */}
        {calledBefore === "no" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-2 space-y-2.5" data-testid="sr-call-block">
            <div className="text-[11.5px] text-amber-900 font-semibold flex items-center gap-1">
              <AlertTriangle size={13} /> Please call the candidate once, then pick the outcome below.
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Call Status *</Label>
              <Select value={callStatus} onValueChange={setCallStatus}>
                <SelectTrigger data-testid="sr-call-status" className="mt-1"><SelectValue placeholder="Select call outcome…" /></SelectTrigger>
                <SelectContent>
                  {["Call Not Answered", "Not Interested", "SPAM", "Schedule the Interview"].map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Remark *</Label>
              <Textarea
                value={callRemark}
                onChange={(e) => setCallRemark(e.target.value)}
                rows={2}
                placeholder="Briefly note what happened on the call…"
                className="mt-1 text-[12px]"
                data-testid="sr-call-remark"
              />
            </div>
          </div>
        )}

        {/* Step 3 — Schedule form (locked unless allowed) */}
        <div className={canSchedule ? "space-y-3" : "space-y-3 opacity-40 pointer-events-none"} data-testid="sr-schedule-block">
          <Fld label="Round *">
            <Select value={form.round} onValueChange={(v) => setForm({ ...form, round: v })}>
              <SelectTrigger data-testid="sr-round"><SelectValue /></SelectTrigger>
              <SelectContent>{ROUNDS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <Fld label="Interviewer *">
            <Select value={form.interviewer_id} onValueChange={(v) => setForm({ ...form, interviewer_id: v })}>
              <SelectTrigger data-testid="sr-interviewer"><SelectValue placeholder="Select interviewer…" /></SelectTrigger>
              <SelectContent>
                {employees.filter((e) => e.is_active).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Date & Time *">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-stretch">
              <Input
                type="date"
                value={dateOnly}
                onChange={(e) => setDateOnly(e.target.value)}
                data-testid="sr-date"
              />
              <Select value={hour12} onValueChange={setHour12}>
                <SelectTrigger className="w-[78px]" data-testid="sr-hour"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((h) => (
                    <SelectItem key={h} value={String(parseInt(h, 10))}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="w-[78px]" data-testid="sr-minute"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={ampm} onValueChange={setAmPm}>
                <SelectTrigger className="w-[78px]" data-testid="sr-ampm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="PM">PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scheduledAt && (
              <div className="text-[11px] text-violet-700 mt-1 mono" data-testid="sr-time-ampm">
                <CalendarClock size={11} className="inline mr-1" />
                {new Date(scheduledAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", hour12: true })}
                {form.round === "Physical Interview" && <span className="ml-2 text-[10.5px] font-semibold text-blue-700">· On-site visit</span>}
              </div>
            )}
          </Fld>
          {/* Physical Interview rounds happen on-site — no meeting link or
              sample questions are required. Hide both fields. */}
          {form.round !== "Physical Interview" && (
            <>
              <Fld label={`Meeting URL${isAdmin ? " *" : ""}`}>
                <Input
                  value={form.meeting_url}
                  onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
                  placeholder="https://meet.google.com/…"
                  data-testid="sr-url"
                />
                {isAdmin && (
                  <div className="text-[10.5px] text-rose-700 mt-1">Meeting link is mandatory for HR / Master Admin.</div>
                )}
              </Fld>
              <Fld label="Sample Questions for Interviewer *">
                <Textarea
                  value={form.sample_questions}
                  onChange={(e) => setForm({ ...form, sample_questions: e.target.value })}
                  rows={3}
                  placeholder={"Q1.\nQ2.\nQ3."}
                  data-testid="sr-samples"
                />
              </Fld>
            </>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            className={
              calledBefore === "no" && callStatus && callStatus !== "Schedule the Interview"
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-violet-600 hover:bg-violet-700"
            }
            data-testid="sr-submit"
          >
            {calledBefore === "no" && callStatus && callStatus !== "Schedule the Interview"
              ? "Save Call Outcome"
              : "Schedule Round"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({ xid, interview, onClose, onSaved }) {
  const initial = {
    communication: "", communication_remark: "",
    technical: "", technical_remark: "",
    problem_solving: "", problem_solving_remark: "",
    confidence: "", confidence_remark: "",
    experience: "", experience_remark: "",
    cultural: "", cultural_remark: "",
    strengths: "", weaknesses: "",
    suitable: "", training_required: "",
    expected_salary: "", notice_period: "", concerns: "",
    recommendation: "", final_remark: "",
    result: "", reason: "", overall_rating: "",
  };
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    for (const k of ["communication", "technical", "problem_solving", "confidence", "experience", "cultural", "suitable", "training_required", "recommendation", "result", "overall_rating"]) {
      if (!f[k]) { toast.error("Please complete all required fields"); return; }
    }
    try {
      await api.post(`/interviews/${xid}/feedback`, { feedback: { ...f, overall_rating: Number(f.overall_rating) } });
      toast.success("Feedback recorded");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
        <DialogHeader><DialogTitle className="heading">Interview Feedback — {interview?.round}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          {[
            ["communication", "Communication Skills"],
            ["technical", "Technical / Subject Knowledge"],
            ["problem_solving", "Problem Solving & Analytical Skills"],
            ["confidence", "Confidence & Personality"],
            ["experience", "Relevant Experience"],
            ["cultural", "Cultural Fit & Attitude"],
          ].map(([k, lbl]) => (
            <div key={k} className="border-b border-slate-100 pb-2">
              <Label className="text-[12px] font-bold text-slate-800">{lbl} *</Label>
              <div className="flex gap-1.5 mt-1 flex-wrap">
                {RATINGS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => set(k, r)}
                    className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold border ${
                      f[k] === r ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200"
                    }`}
                    data-testid={`fb-${k}-${r}`}
                  >{r}</button>
                ))}
              </div>
              <Input value={f[k + "_remark"]} onChange={(e) => set(k + "_remark", e.target.value)} placeholder="Remarks (optional)" className="mt-1.5 h-8 text-[11.5px]" />
            </div>
          ))}

          <Fld label="What are the candidate's strengths?">
            <Textarea value={f.strengths} onChange={(e) => set("strengths", e.target.value)} rows={2} />
          </Fld>
          <Fld label="What are the candidate's weaknesses / improvement areas?">
            <Textarea value={f.weaknesses} onChange={(e) => set("weaknesses", e.target.value)} rows={2} />
          </Fld>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Suitable for the role? *">
              <Select value={f.suitable} onValueChange={(v) => set("suitable", v)}>
                <SelectTrigger data-testid="fb-suitable"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{["Yes", "No", "Partially"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Additional training required? *">
              <Select value={f.training_required} onValueChange={(v) => set("training_required", v)}>
                <SelectTrigger data-testid="fb-training"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{["Yes", "No"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Expected Salary (if discussed)">
              <Input value={f.expected_salary} onChange={(e) => set("expected_salary", e.target.value)} placeholder="e.g. ₹8 LPA" />
            </Fld>
            <Fld label="Notice Period / Joining Availability">
              <Input value={f.notice_period} onChange={(e) => set("notice_period", e.target.value)} placeholder="e.g. 30 days" />
            </Fld>
          </div>
          <Fld label="Any concerns regarding the candidate?">
            <Textarea value={f.concerns} onChange={(e) => set("concerns", e.target.value)} rows={2} />
          </Fld>
          <Fld label="Recommendation for next round *">
            <Select value={f.recommendation} onValueChange={(v) => set("recommendation", v)}>
              <SelectTrigger data-testid="fb-recommendation"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{RECOMMENDATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Interview Result *">
              <Select value={f.result} onValueChange={(v) => set("result", v)}>
                <SelectTrigger data-testid="fb-result"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{RESULTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Overall Rating (out of 10) *">
              <Input type="number" min="1" max="10" value={f.overall_rating} onChange={(e) => set("overall_rating", e.target.value)} data-testid="fb-rating" />
            </Fld>
          </div>
          <Fld label="Reason for Decision">
            <Textarea value={f.reason} onChange={(e) => set("reason", e.target.value)} rows={2} />
          </Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fb-submit">Submit Feedback</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewFeedbackDialog({ interview, onClose }) {
  if (!interview) return null;
  const f = interview.feedback || {};
  const rubric = [
    ["communication", "Communication Skills"],
    ["technical", "Technical / Subject Knowledge"],
    ["problem_solving", "Problem Solving & Analytical Skills"],
    ["confidence", "Confidence & Personality"],
    ["experience", "Relevant Experience"],
    ["cultural", "Cultural Fit & Attitude"],
  ];
  const ratingClass = (v) =>
    v === "Excellent" ? "bg-emerald-100 text-emerald-800" :
    v === "Good" ? "bg-violet-100 text-violet-800" :
    v === "Average" ? "bg-amber-100 text-amber-800" :
    v === "Poor" ? "bg-rose-100 text-rose-800" :
    "bg-slate-100 text-slate-700";
  const summaryFields = [
    ["Strengths", f.strengths],
    ["Weaknesses", f.weaknesses],
    ["Suitable", f.suitable],
    ["Training Required", f.training_required],
    ["Expected Salary", f.expected_salary],
    ["Notice Period", f.notice_period],
    ["Concerns", f.concerns],
    ["Reason for Decision", f.reason],
    ["Final Remark", f.final_remark],
  ];
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="heading">
            Interview Feedback — {interview.round}
          </DialogTitle>
          <div className="text-[11.5px] text-slate-500">
            {interview.interviewer_name} · {(() => {
              try { return new Date(interview.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
              catch { return interview.scheduled_at; }
            })()}
          </div>
        </DialogHeader>
        {!f.result ? (
          <div className="text-slate-500 italic py-8 text-center">No feedback recorded for this round yet.</div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex flex-wrap gap-4 text-[13px]">
              <div><b>Result:</b> {f.result}</div>
              <div><b>Overall Rating:</b> {f.overall_rating}/10</div>
              <div><b>Recommendation:</b> {f.recommendation || "—"}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Rubric Scores</div>
              <ul className="space-y-2">
                {rubric.map(([k, lbl]) => (
                  <li key={k} className="border-b border-slate-100 pb-1.5 last:border-0" data-testid={`vf-${k}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[12.5px] text-slate-800 font-medium">{lbl}</div>
                      <Badge className={`${ratingClass(f[k])} text-[10.5px]`}>{f[k] || "—"}</Badge>
                    </div>
                    {f[`${k}_remark`] && (
                      <div className="text-[11.5px] text-slate-600 mt-0.5 italic">{f[`${k}_remark`]}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              {summaryFields.map(([label, value]) => (
                <div key={label} className="grid grid-cols-[140px_1fr] gap-2 text-[12.5px] py-1">
                  <div className="text-slate-500 uppercase text-[10.5px] tracking-wider font-semibold pt-0.5">{label}</div>
                  <div className="text-slate-800 whitespace-pre-wrap">
                    {value || <span className="text-slate-400 italic">—</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="vf-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MoveToOnboardingDialog({ ie, onClose, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ offered_salary: "", joining_date: "", company: "" });
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data || [])); }, []);
  const submit = async () => {
    if (!form.offered_salary || Number(form.offered_salary) <= 0) return toast.error("Enter the agreed salary");
    if (!form.joining_date) return toast.error("Pick a joining date");
    try {
      await api.post(`/interviewees/${ie.id}/move-to-onboarding`, {
        offered_salary: Number(form.offered_salary),
        joining_date: form.joining_date,
        company: form.company,
      });
      toast.success("Moved to Onboarding");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="move-onb-dialog">
        <DialogHeader><DialogTitle className="heading">Move to Onboarding — {ie.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <Fld label="Agreed Monthly Salary (₹) *">
            <Input type="number" value={form.offered_salary} onChange={(e) => setForm({ ...form, offered_salary: e.target.value })} placeholder="e.g. 50000" data-testid="mv-salary" />
          </Fld>
          <Fld label="Date of Joining *">
            <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="mv-doj" />
          </Fld>
          <Fld label="Company">
            <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
              <SelectTrigger data-testid="mv-company"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="text-[11.5px] text-slate-600 italic">
            After moving, HR can generate temporary credentials for the candidate from the Onboarding module.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mv-submit">Move</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


// =============== Edit Interviewee dialog (Admin only) ===============
function EditInterviewee({ interviewee, leadSources, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: interviewee.name || "",
    personal_email: interviewee.personal_email || "",
    mobile_number: interviewee.mobile_number || "",
    position: interviewee.position || "",
    resume_link: interviewee.resume_link || "",
    source_of_lead: interviewee.source_of_lead || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [resumeMeta, setResumeMeta] = useState({
    filename: interviewee.resume_filename || "",
    uploaded_at: interviewee.resume_uploaded_at || "",
    has_gridfs: !!interviewee.resume_gridfs_id,
  });

  const uploadResume = async (file) => {
    if (!file) return;
    // Accept: PDF, DOC/DOCX, common image types. 15 MB cap enforced by backend too.
    const okTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/png", "image/jpeg", "image/webp"];
    if (okTypes.length && !okTypes.includes(file.type)) {
      return toast.error("Only PDF, DOC, DOCX or image resumes are allowed");
    }
    if (file.size > 15 * 1024 * 1024) {
      return toast.error("Resume must be ≤ 15 MB");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/interviewees/${interviewee.id}/resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, resume_link: data.resume_link || f.resume_link }));
      setResumeMeta({
        filename: data.resume_filename || file.name,
        uploaded_at: data.resume_uploaded_at || new Date().toISOString(),
        has_gridfs: true,
      });
      toast.success("Resume uploaded");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeResume = async () => {
    if (!resumeMeta.has_gridfs) {
      setForm((f) => ({ ...f, resume_link: "" }));
      return;
    }
    if (!window.confirm("Remove the uploaded resume?")) return;
    setUploading(true);
    try {
      await api.delete(`/interviewees/${interviewee.id}/resume`);
      setResumeMeta({ filename: "", uploaded_at: "", has_gridfs: false });
      setForm((f) => ({ ...f, resume_link: "" }));
      toast.success("Resume removed");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadResume(f);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.personal_email.trim() || !form.mobile_number.trim()) {
      return toast.error("Name, email and mobile number are required");
    }
    setSaving(true);
    try {
      await api.patch(`/interviewees/${interviewee.id}`, form);
      toast.success("Candidate details updated");
      onSaved();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && !uploading && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ie-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={15} /> Edit Candidate</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Full Name *</Label>
            <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ie-edit-name" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Personal Email *</Label>
            <Input type="email" className="mt-1 mono" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} data-testid="ie-edit-email" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Mobile Number *</Label>
            <Input className="mt-1 mono" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} data-testid="ie-edit-mobile" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Position</Label>
            <Input className="mt-1" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="ie-edit-position" />
          </div>
          {/* Resume: drag-drop or link */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Resume</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={`mt-1 border-2 border-dashed rounded-lg p-3 text-center transition-colors ${dragActive ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40"}`}
              data-testid="ie-edit-resume-dropzone"
            >
              {resumeMeta.has_gridfs ? (
                <div className="flex items-center justify-between gap-2 text-[12px]">
                  <a
                    href={form.resume_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-violet-700 dark:text-violet-300 hover:underline font-semibold min-w-0 truncate"
                    data-testid="ie-edit-resume-open"
                  >
                    <Paperclip size={12} />
                    <span className="truncate">{resumeMeta.filename || "Resume"}</span>
                  </a>
                  <div className="flex items-center gap-1 shrink-0">
                    <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="ie-edit-resume-replace">
                      <Upload size={11} /> Replace
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" onChange={(e) => e.target.files?.[0] && uploadResume(e.target.files[0])} disabled={uploading} />
                    </label>
                    <button type="button" onClick={removeResume} disabled={uploading} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20" data-testid="ie-edit-resume-remove">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-1 py-2" data-testid="ie-edit-resume-picker">
                  <Upload size={18} className="text-slate-400" />
                  <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                    {uploading ? "Uploading…" : "Drop resume here or click to select"}
                  </span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">PDF · DOC · DOCX · Image · ≤ 15 MB</span>
                  <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" onChange={(e) => e.target.files?.[0] && uploadResume(e.target.files[0])} disabled={uploading} />
                </label>
              )}
            </div>
            <Input
              className="mt-2 mono text-[12px]"
              value={form.resume_link}
              onChange={(e) => setForm({ ...form, resume_link: e.target.value })}
              placeholder="…or paste an external resume URL"
              data-testid="ie-edit-resume-link"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Source of Lead</Label>
            <Select value={form.source_of_lead || undefined} onValueChange={(v) => setForm({ ...form, source_of_lead: v })}>
              <SelectTrigger className="mt-1" data-testid="ie-edit-source"><SelectValue placeholder="Select source…" /></SelectTrigger>
              <SelectContent>
                {(leadSources || []).map((s) => (
                  <SelectItem key={s.id || s.name || s} value={s.name || s}>{s.name || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={saving || uploading} onClick={onClose}>Cancel</Button>
          <Button disabled={saving || uploading} onClick={submit} data-testid="ie-edit-submit">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   CandidateDetailsView — render EVERY field captured on the
   interviewee doc, grouped into semantic sections. Falls back
   to a free-form "Other" section for any unrecognised key, so
   future fields appear automatically without code changes.
   ============================================================ */
const DETAIL_GROUPS = [
  ["Identity", [
    ["name", "Full Name"],
    ["dob", "Date of Birth"],
    ["gender", "Gender"],
    ["marital_status", "Marital Status"],
    ["father_name", "Father's Name"],
    ["mother_name", "Mother's Name"],
    ["nationality", "Nationality"],
    ["blood_group", "Blood Group"],
  ]],
  ["Contact", [
    ["personal_email", "Personal Email"],
    ["alt_email", "Alternate Email"],
    ["mobile_number", "Mobile"],
    ["alt_mobile_number", "Alternate Mobile"],
    ["whatsapp", "WhatsApp"],
    ["current_address", "Current Address"],
    ["permanent_address", "Permanent Address"],
    ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
    ["country", "Country"],
  ]],
  ["Position & Source", [
    ["position", "Position Applied For"],
    ["department", "Department"],
    ["source_of_lead", "Source of Lead"],
    ["referred_by", "Referred By"],
    ["current_round", "Current Round"],
    ["status", "Pipeline Status"],
    ["next_step", "Next Step"],
  ]],
  ["Compensation & Notice", [
    ["current_salary", "Current Salary"],
    ["expected_salary", "Expected Salary"],
    ["agreed_salary", "Agreed Salary"],
    ["notice_period", "Notice Period"],
    ["earliest_join_date", "Earliest Join Date"],
    ["availability", "Availability"],
  ]],
  ["Education", [
    ["highest_qualification", "Highest Qualification"],
    ["qualification", "Qualification"],
    ["specialization", "Specialization"],
    ["college", "College / University"],
    ["passing_year", "Passing Year"],
    ["percentage", "Percentage / CGPA"],
  ]],
  ["Experience", [
    ["total_experience", "Total Experience"],
    ["relevant_experience", "Relevant Experience"],
    ["current_company", "Current Company"],
    ["previous_company", "Previous Company"],
    ["current_designation", "Current Designation"],
    ["key_skills", "Key Skills"],
    ["domain_expertise", "Domain Expertise"],
  ]],
  ["Documents & Links", [
    ["resume_link", "Resume"],
    ["portfolio_link", "Portfolio"],
    ["linkedin", "LinkedIn"],
    ["github", "GitHub"],
    ["aadhaar", "Aadhaar"],
    ["pan", "PAN"],
  ]],
  ["Audit", [
    ["created_by_name", "Created By"],
    ["created_at", "Created At"],
    ["no_show_count", "No-Show Count"],
  ]],
];

const HIDDEN_KEYS = new Set([
  "id", "_id", "remarks", "next_step", // shown in other tabs / header
]);

function isUrl(v) { return typeof v === "string" && /^https?:\/\//i.test(v); }

function CandidateDetailsView({ viewing }) {
  if (!viewing) return null;
  const handled = new Set();
  DETAIL_GROUPS.forEach(([, keys]) => keys.forEach(([k]) => handled.add(k)));
  const otherEntries = Object.entries(viewing).filter(([k, v]) => {
    if (HIDDEN_KEYS.has(k) || handled.has(k)) return false;
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return false;
    return true;
  });

  const render = (k, label) => {
    let v = viewing[k];
    if (v === null || v === undefined || (typeof v === "string" && !v.trim())) return null;
    if (k === "created_at" && v) {
      try { v = new Date(v).toLocaleString("en-IN"); } catch (e) { /* keep */ }
    }
    return (
      <div key={k} className="rounded-md border border-slate-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))] p-2.5">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
        <div className="text-[12.5px] mt-1 break-words text-slate-800 dark:text-slate-100 whitespace-pre-wrap font-medium">
          {Array.isArray(v) ? v.join(", ") : (isUrl(v) ? <a href={v} target="_blank" rel="noreferrer" className="text-violet-700 dark:text-violet-300 underline break-all">{v}</a> : String(v))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="ie-candidate-details">
      {DETAIL_GROUPS.map(([title, keys]) => {
        const cells = keys.map(([k, label]) => render(k, label)).filter(Boolean);
        if (cells.length === 0) return null;
        return (
          <section key={title}>
            <div className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-violet-700 dark:text-violet-300 mb-2 flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-violet-500 dark:bg-violet-400" data-keep-color /> {title}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">{cells}</div>
          </section>
        );
      })}
      {otherEntries.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-slate-500" data-keep-color /> Other
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {otherEntries.map(([k, v]) => render(k, k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())))}
          </div>
        </section>
      )}
    </div>
  );
}


